import React, { useState, useEffect } from "react";
import { Table, Spin, message, Button, Input, Space } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";

import { fetchStudents, setDefaultPassword, fetchStudentDetails } from "../../ApiServices/getStudentService";
import { getStudentTableColumns } from "../../Config/studentTableColumns";
import axiosInstance from "../../Config/axiosConfig";

import StudentDetailsModal from "../../Components/StudentDetailsModal/StudentDetailsModal";
import './Students.css'

const Students = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  
  // New state for education classes lookup
  const [educationClasses, setEducationClasses] = useState([]);

  // Fetch education classes lookup data
  const fetchEducationClasses = async () => {
    try {
      const response = await axiosInstance.post('Public/get-lookups');
      console.log("lookups response:", response.data.data.educationClasses);
      if (response.data && response.data.data.educationClasses) {
        setEducationClasses(response.data.data.educationClasses);
        console.log("Education classes loaded:", response.data.data.educationClasses);
      }
    } catch (error) {
      console.error('Error fetching education classes:', error);
    }
  };
  
// In your Students.jsx component, update the loadStudents function:
const loadStudents = async () => {
  try {
    setLoading(true);
    setError(null);

    // First fetch education classes
    await fetchEducationClasses();

    // Then fetch students
    const studentsData = await fetchStudents();
    console.log('students list', studentsData);
    console.log('education classes', educationClasses); // Check if loaded
    
    const sortedStudents = studentsData.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    setStudents(sortedStudents);
    setFilteredStudents(sortedStudents);

    message.success("Students loaded");
  } catch (error) {
    console.error(error);
    setError(error.message);
    message.error("Failed to load students");
  } finally {
    setLoading(false);
  }
};

  const handleSearch = (value) => {
    setSearchText(value);
    const keyword = value.toLowerCase().trim();

    if (keyword === "") {
      setFilteredStudents(students);
      return;
    }

    const filtered = students.filter((student) => {
      const fullName = (
        student.full_name ||
        student.name ||
        `${student.firstName || ""} ${student.lastName || ""}`.trim()
      ).toLowerCase();

      let className = "";
      if (student.education_class_id && educationClasses.length > 0) {
        const classItem = educationClasses.find(
          (item) => item.id === student.education_class_id
        );
        if (classItem) className = classItem.name.toLowerCase();
      }
      console.log('className for filtering:', className);
      return (
        fullName.includes(keyword) ||
        (student.email || "").toLowerCase().includes(keyword) ||
        (student.phone_number || "").toLowerCase().includes(keyword) ||
        (student.education_class || "").toLowerCase().includes(keyword) ||
        className.includes(keyword)
      );
    });

    setFilteredStudents(filtered);
  };

  const handleViewDetails = async (student) => {
    setDetailsLoading(true);
    setSelectedStudent(null);
    setIsModalVisible(true);

    try {
      const details = await fetchStudentDetails(student.id);
      setSelectedStudent({ 
        ...details, 
        full_name: student.name, 
        registration_date: student.account_start 
      });
    } catch (error) {
      message.error("Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSetDefault = async (student) => {
    try {
      const ok = window.confirm(
        `Reset password for ${student.name}?`
      );
      if (!ok) return;

      await setDefaultPassword(student.id);
      alert("Password reset successfully");
    } catch (error) {
      alert("Failed to reset password");
    }
  };

  const getEnhancedColumns = () => {
    // Pass educationClasses to getStudentTableColumns
    const baseColumns = getStudentTableColumns(educationClasses);

    return [
      {
        title: "#",
        width: 50,
        render: (_, __, index) => index + 1,
      },
      ...baseColumns,
      {
        title: "Actions",
        width: 180,
        render: (_, record) => (
          <Space>
            <Button type="link" onClick={() => handleViewDetails(record)}>View</Button>
            <Button danger type="link" onClick={() => handleSetDefault(record)}>Reset</Button>
          </Space>
        ),
      },
    ];
  };

  useEffect(() => {
    loadStudents();
  }, []);

  return (
    <div className="student-admin-container">

      {/* Header */}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
        <Space>
          <Button type="primary" icon={<ReloadOutlined />} onClick={loadStudents} loading={loading}>
            Refresh
          </Button>

          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>

        <div>Total: {students.length}</div>
      </div>

      {/* Table */}
      <Spin spinning={loading}>
        <Table
          columns={getEnhancedColumns()}
          dataSource={filteredStudents}
          rowKey={(r) => r.id}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Spin>

      {/* Modal */}
      <StudentDetailsModal
        visible={isModalVisible}
        loading={detailsLoading}
        student={selectedStudent}
        onClose={() => setIsModalVisible(false)}
      />
    </div>
  );
};

export default Students;